import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import Editor from "@monaco-editor/react";
import {
  Box,
  Typography,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  Menu,
  Stack,
} from "@mui/material";
import {
  AccessTimeOutlined,
  PlayArrow,
  Send,
  ExpandMore,
  ExpandLess,
  DarkModeOutlined,
  LightModeOutlined,
  FormatIndentIncrease,
  OpenInFull,
  CloseFullscreen,
  Terminal,
  MoreVert,
  InfoOutlined,
} from "@mui/icons-material";
import {
  formatCode,
  formatSelection,
} from "../../utils/programming/monacoEditorUtils";
import { registerLanguageFormatters } from "../../utils/programming/codeFormatters";
import {
  getBaseEditorOptions,
  EDITOR_TAB_SIZE,
} from "../../utils/programming/monacoEditorConfig";
import OutputPanel from "./OutputPanel";
import AttemptsInfoModal from "./AttemptsInfoModal";

/**
 * Saved Badge Component
 */
const SavedBadge = ({ saveState, lastSavedAt }) => {
  if (!saveState) return null;

  let borderColor = "#e0e0e0";
  let bgcolor = "#f5f5f5";
  let color = "#666";
  let text = "";

  if (saveState === "saving") {
    text = "Saving…";
    borderColor = "#FFF9C4";
    bgcolor = "#FFFDE7";
    color = "#FBC02D";
  } else if (saveState === "saved") {
    text = `Saved${lastSavedAt ? ` · ${lastSavedAt.toLocaleTimeString()}` : ""}`;
    borderColor = "#C8E6C9";
    bgcolor = "#E8F5E9";
    color = "#2E7D32";
  } else if (saveState === "error") {
    text = "Save failed";
    borderColor = "#FFCDD2";
    bgcolor = "#FFEBEE";
    color = "#C62828";
  } else {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        height: "32px",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        bgcolor: bgcolor,
        color: color,
      }}
    >
      <Typography variant="caption" fontWeight={500}>
        {text}
      </Typography>
    </Box>
  );
};

/**
 * Editor Section Component - Contains Monaco Editor, header, language selector, and action buttons
 */
const EditorSection = ({
  code,
  setCode,
  selectedLanguage,
  setSelectedLanguage,
  editorTheme,
  setEditorTheme,
  isRunningCode,
  isSubmitting,
  attemptsExhausted,
  attemptsUsed,
  maxAttempts,
  supportedLanguages,
  onRunCode,
  onSubmit,
  onFormatCode,
  saveState,
  lastSavedAt,
  timeRemaining,
  showOutput,
  setShowOutput,
  isOutputFloating,
  editorRef,
  monacoRef,
  trackEditorEvent,
  lastChangeEventTimeRef,
  lastCursorEventTimeRef,
  lastSelectionEventTimeRef,
  lastFocusEventTimeRef,
  CHANGE_THROTTLE_MS,
  CURSOR_THROTTLE_MS,
  SELECTION_THROTTLE_MS,
  FOCUS_THROTTLE_MS,
  typingAnalyzer,
  componentContainerRef,
  monacoLanguage,
  onLanguageChange,
  activeTab,
  setActiveTab,
  testCases,
  customInput,
  setCustomInput,
  customResult,
  customLoading,
  onRunCustomInput,
  results,
  summary,
  showCelebration,
  setShowCelebration,
  onFloatingToggle,
  vtLoading,
  outputHeight = 300,
  isMobile = false,
  questionId,
  isFirstProgrammingQuestion = false,
  onModalStateChange,
  isReadOnly,
  isAnswered,
}) => {
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const [editorWidthPercent, setEditorWidthPercent] = useState(60);
  const [isResizing, setIsResizing] = useState(false);
  const resizingRef = useRef(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [openAttemptsInfoModal, setOpenAttemptsInfoModal] = useState(false);
  const [isAutoOpened, setIsAutoOpened] = useState(false);
  const isEditorChangeRef = useRef(false);
  const editorValueRef = useRef(code);
  const onChangeTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const resetFlagTimeoutRef = useRef(null);
  const MIN_EDITOR_WIDTH = 400;
  const MIN_OUTPUT_WIDTH = 300;

  const [isMonacoMounted, setIsMonacoMounted] = useState(false);
  const settings = useSelector((state) => state.testReducer?.setting);

  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs < 10 ? "0" + hrs : hrs}:${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
  };

  const isCopyPasteDisabled = useMemo(() => {
    return settings?.some(
      (s) =>
        s.setting === "Turn off copy paste from external sources" && s.active,
    );
  }, [settings]);

  useEffect(() => {
    if (
      isMonacoMounted &&
      editorRef.current &&
      monacoRef.current &&
      isCopyPasteDisabled
    ) {
      const editor = editorRef.current;
      const domNode = editor.getDomNode();

      const preventEvent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      // Block copy/paste/cut/contextmenu at the DOM level (capture phase)
      if (domNode) {
        domNode.addEventListener("paste", preventEvent, true);
        domNode.addEventListener("copy", preventEvent, true);
        domNode.addEventListener("cut", preventEvent, true);
        domNode.addEventListener("contextmenu", preventEvent, true);
      }

      // Also try to disable via keybinding commands
      // Note: addCommand returns an IDisposable
      const disposal1 = editor.addCommand(
        monacoRef.current.KeyMod.CtrlCmd | monacoRef.current.KeyCode.KeyC,
        () => {},
      );
      const disposal2 = editor.addCommand(
        monacoRef.current.KeyMod.CtrlCmd | monacoRef.current.KeyCode.KeyV,
        () => {},
      );
      const disposal3 = editor.addCommand(
        monacoRef.current.KeyMod.CtrlCmd | monacoRef.current.KeyCode.KeyX,
        () => {},
      );

      return () => {
        if (domNode) {
          domNode.removeEventListener("paste", preventEvent, true);
          domNode.removeEventListener("copy", preventEvent, true);
          domNode.removeEventListener("cut", preventEvent, true);
          domNode.removeEventListener("contextmenu", preventEvent, true);
        }

        // Safer disposal: only call dispose if it exists as a function
        // Monaco addCommand returns a string in some versions, not an IDisposable
        if (disposal1 && typeof disposal1.dispose === "function")
          disposal1.dispose();
        if (disposal2 && typeof disposal2.dispose === "function")
          disposal2.dispose();
        if (disposal3 && typeof disposal3.dispose === "function")
          disposal3.dispose();
      };
    }
  }, [isCopyPasteDisabled, isMonacoMounted]);

  useEffect(() => {
    if (!isFirstProgrammingQuestion) return;

    const testSessionId = sessionStorage.getItem("currentTestSessionId");
    const modalShownKey = `attemptsModalShown_${testSessionId}`;
    const hasShownModal = localStorage.getItem(modalShownKey);

    if (!hasShownModal) {
      const timer = setTimeout(() => {
        setIsAutoOpened(true);
        setOpenAttemptsInfoModal(true);
        localStorage.setItem(modalShownKey, "true");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isFirstProgrammingQuestion, questionId]);

  useEffect(() => {
    return () => {
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current);
      }
      if (resetFlagTimeoutRef.current) {
        clearTimeout(resetFlagTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      editorValueRef.current = code;
      return;
    }

    if (isEditorChangeRef.current || !editorRef.current) {
      return;
    }

    const model = editorRef.current.getModel();
    if (!model) return;

    const currentEditorValue = model.getValue();

    const isExternalChange =
      currentEditorValue !== code &&
      editorValueRef.current !== code &&
      Math.abs(currentEditorValue.length - code.length) > 10;
    if (isExternalChange) {
      const position = editorRef.current.getPosition();
      const selection = editorRef.current.getSelection();
      model.setValue(code);
      editorValueRef.current = code;

      if (position) {
        const lineCount = model.getLineCount();
        const targetLine = Math.min(position.lineNumber, lineCount);
        const targetColumn = Math.min(
          position.column,
          model.getLineLength(targetLine) + 1,
        );
        requestAnimationFrame(() => {
          if (editorRef.current) {
            editorRef.current.setPosition({
              lineNumber: targetLine,
              column: targetColumn,
            });
          }
        });
      }

      if (selection && !selection.isEmpty()) {
        requestAnimationFrame(() => {
          if (editorRef.current) {
            editorRef.current.setSelection(selection);
          }
        });
      }
    }
  }, [code]);

  useEffect(() => {
    if (editorRef.current && monacoRef.current && monacoLanguage) {
      const model = editorRef.current.getModel();
      if (model) {
        monacoRef.current.editor.setModelLanguage(model, monacoLanguage);
        // A new model brings its own indent settings, which win over the
        // editor's — restate them or Enter indents by whatever the previous
        // language was using.
        editorRef.current.updateOptions({
          tabSize: EDITOR_TAB_SIZE,
          detectIndentation: false,
        });
        model.updateOptions({
          tabSize: EDITOR_TAB_SIZE,
          indentSize: EDITOR_TAB_SIZE,
          insertSpaces: true,
        });
      }
    }
  }, [monacoLanguage]);

  useEffect(() => {
    if (!isEditorFullscreen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setIsEditorFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isEditorFullscreen]);

  const toggleFullscreen = () => {
    setIsEditorFullscreen((prev) => {
      if (prev) {
        setEditorWidthPercent(60);
      }
      return !prev;
    });
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    setIsResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  useEffect(() => {
    if (!isEditorFullscreen) return;

    const handleMouseMove = (e) => {
      if (!resizingRef.current) return;

      const containerWidth = window.innerWidth;
      const mouseX = e.clientX;
      const editorWidthPercentNew = (mouseX / containerWidth) * 100;

      const minEditorPercent = (MIN_EDITOR_WIDTH / containerWidth) * 100;
      const maxEditorPercent =
        ((containerWidth - MIN_OUTPUT_WIDTH) / containerWidth) * 100;

      const clampedPercent = Math.max(
        minEditorPercent,
        Math.min(maxEditorPercent, editorWidthPercentNew),
      );

      setEditorWidthPercent(clampedPercent);
    };

    const handleMouseUp = () => {
      if (resizingRef.current) {
        resizingRef.current = false;
        setIsResizing(false);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };

    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isEditorFullscreen, isResizing]);

  const editorOptions = useMemo(
    () => ({
      ...getBaseEditorOptions(isMobile),
      readOnly: isReadOnly, // Only lock if explicitly readOnly (e.g. reviewed/submitted), NOT when attempts exhausted
      minimap: { enabled: false },
      scrollbar: {
        vertical: "auto",
        horizontal: "auto",
        verticalScrollbarSize: isMobile ? 12 : 8,
        horizontalScrollbarSize: isMobile ? 12 : 8,
      },
      automaticLayout: true,
      scrollBeyondLastLine: false,
      wordWrap: "on",
      padding: { top: 16, bottom: 16 },
      formatOnSave: true,
      formatOnType: false,
      formatOnPaste: false,
      autoClosingBrackets: "languageDefined",
      autoClosingQuotes: "languageDefined",
      autoSurround: "languageDefined",
      bracketPairColorization: { enabled: true },
      guides: { indentation: true, bracketPairs: true },
      mouseWheelZoom: !isMobile,
      quickSuggestions: !isMobile,
      suggestOnTriggerCharacters: !isMobile,
      acceptSuggestionOnEnter: "on",
      multiCursorModifier: isMobile ? "ctrlCmd" : "alt",
      dragAndDrop: !isMobile,
      quickSuggestionsDelay: 300,
      suggest: {
        showKeywords: true,
        showSnippets: true,
        maxVisibleSuggestions: 12,
      },
      renderWhitespace: "none",
      renderLineHighlight: "none",
      occurrencesHighlight: false,
      renderIndentGuides: false,
      links: false,
      colorDecorators: false,
      codeLens: false,
    }),
    [isReadOnly, isMobile],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        py: 0,
        bgcolor: "#fff",
        borderRadius: isEditorFullscreen ? 0 : "4px",
        minHeight: isMobile ? "300px" : 0,
        height: "100%",
        position: isEditorFullscreen ? "fixed" : "relative",
        top: isEditorFullscreen ? 0 : "auto",
        left: isEditorFullscreen ? 0 : "auto",
        right: isEditorFullscreen ? 0 : "auto",
        bottom: isEditorFullscreen ? 0 : "auto",
        zIndex: isEditorFullscreen ? 9999 : "auto",
        display: "flex",
        flexDirection: "column",
        overflowY: isMobile ? "auto" : "hidden",
        overflowX: "hidden",
      }}
    >
      {/* Header - Reorganized for Fullscreen Mode */}
      {isEditorFullscreen ? (
        /* Fullscreen Header Layout - Optimized single row */
        <Box
          sx={{
            display: "flex",
            flexDirection: { base: "column", sm: "row" }, // Stack on small mobile, row on tablet+
            justifyContent: "space-between",
            alignItems: { base: "stretch", sm: "center" }, // Stretch on mobile
            gap: { base: 1.5, sm: 2 },
            mb: 1,
            px: { base: 1, sm: 4 }, // Reduce padding on mobile
            pt: { base: 1, sm: 1 },
            flexWrap: "wrap",
            position: "relative",
            zIndex: 10001,
            bgcolor: "#fff",
            mt: 2,
            borderBottom: { base: "1px solid #e0e0e0", sm: "none" }, // Add separator on mobile
            pb: { base: 1, sm: 0 },
          }}
        >
          {/* Left Section: Language Selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              Language:
            </Typography>
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={selectedLanguage}
                onChange={(e) => {
                  const newLanguageId = e.target.value;
                  onLanguageChange(newLanguageId);
                  setSelectedLanguage(newLanguageId);
                }}
                disabled={isReadOnly}
                size="small"
                sx={{
                  borderRadius: "8px",
                  height: "36px",
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 10002,
                    },
                  },
                  style: {
                    zIndex: 10002,
                  },
                }}
              >
                {supportedLanguages.map((language) => (
                  <MenuItem
                    key={language.languageId}
                    value={language.languageId}
                  >
                    {language.languageName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Center Section: Status Indicators */}
          <Box
            sx={{
              display: "flex",
              gap: { base: 1, sm: 1.5 },
              alignItems: "center",
              flex: { base: "none", sm: 1 }, // Don't stretch on mobile
              justifyContent: { base: "flex-start", sm: "center" }, // Left align on mobile
              flexWrap: "wrap",
              width: { base: "100%", sm: "auto" },
            }}
          >
            {/* ✅ REMOVED: Total Time and Remaining Time displays */}
            {/* Programming questions no longer have per-question time limits */}

            {/* Overall Remaining Time - Shown in Fullscreen since main header is hidden */}
            {timeRemaining !== undefined && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  bgcolor: "#E3F2FD",
                  color: "#1976D2",
                  px: "10px",
                  py: "6px",
                  borderRadius: "24px",
                  border: "1px solid #BBDEFB",
                }}
              >
                <AccessTimeOutlined sx={{ fontSize: "18px" }} />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatTime(timeRemaining)}
                </Typography>
              </Box>
            )}

            {/* Attempts */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bgcolor: attemptsExhausted ? "#FFEBEE" : "#FFF3E0",
                color: attemptsExhausted ? "#C62828" : "#F57C00",
                px: "10px",
                py: "6px",
                borderRadius: "24px",
                border: attemptsExhausted
                  ? "1px solid #FFCDD2"
                  : "1px solid #FFE0B2",
              }}
            >
              <AccessTimeOutlined sx={{ fontSize: "18px" }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}
              >
                {attemptsUsed}/{maxAttempts} Attempts
              </Typography>
            </Box>

            {/* Saved Badge */}
            <SavedBadge saveState={saveState} lastSavedAt={lastSavedAt} />

            <Tooltip title="Attempts Info" placement="top">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#FF9800",
                  bgcolor: "#FFF3E0",
                  width: "44px",
                  height: "44px",
                  borderRadius: "20px",
                  minHeight: "44px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setIsAutoOpened(false);
                  setOpenAttemptsInfoModal(true);
                }}
              >
                <InfoOutlined sx={{ fontSize: "24px" }} />
              </Box>
            </Tooltip>
          </Box>

          {/* Right Section: Action & Control Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
              width: { base: "100%", sm: "auto" },
              justifyContent: { base: "space-between", sm: "flex-end" },
              flexWrap: "wrap",
            }}
          >
            {/* Run Code Button */}
            <Tooltip title="Run Code (Ctrl+Enter)" placement="top">
              <Box>
                {" "}
                {/* Wrap in Box to support tooltip on disabled button if needed, though button handles it usually */}
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={onRunCode}
                  disabled={
                    isRunningCode ||
                    isSubmitting ||
                    attemptsExhausted ||
                    isReadOnly
                  }
                  sx={{
                    bgcolor:
                      isRunningCode || isSubmitting || attemptsExhausted
                        ? "#E0E0E0"
                        : "#1976D2",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderRadius: "8px",
                    height: "36px",
                    minWidth: "fit-content",
                    px: 1.5,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor:
                        isRunningCode || isSubmitting || attemptsExhausted
                          ? "#E0E0E0"
                          : "#1565C0",
                    },
                    "&:disabled": {
                      bgcolor: "#E0E0E0",
                      color: "#9E9E9E",
                    },
                  }}
                >
                  {isRunningCode ? "Running..." : "Run"}
                </Button>
              </Box>
            </Tooltip>

            {/* Submit Button */}
            <Tooltip title="Submit Solution" placement="top">
              <Box>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={onSubmit}
                  disabled={isRunningCode || isSubmitting || !!isAnswered}
                  sx={{
                    bgcolor:
                      isRunningCode || isSubmitting ? "#E0E0E0" : "#00C49A",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderRadius: "8px",
                    height: "36px",
                    minWidth: "fit-content",
                    px: 1.5,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor:
                        isRunningCode || isSubmitting ? "#E0E0E0" : "#00A085",
                    },
                    "&:disabled": {
                      bgcolor: "#E0E0E0",
                      color: "#9E9E9E",
                    },
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Box>
            </Tooltip>

            {/* Divider */}
            <Box
              sx={{
                width: "1px",
                height: "24px",
                bgcolor: "#e0e0e0",
                mx: 0.5,
              }}
            />

            {/* Format Code Button - Moved Here */}
            <Tooltip
              title="Format Code (Ctrl+Shift+F)"
              placement="top"
              PopperProps={{
                style: { zIndex: 10000 },
              }}
            >
              <IconButton
                onClick={onFormatCode}
                disabled={isReadOnly}
                size="small"
                sx={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "8px",
                  border: "2px solid rgb(143, 142, 142)",
                  "&:hover": {
                    borderColor: "#00C49A",
                    color: "#00C49A",
                  },
                }}
              >
                <FormatIndentIncrease fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Theme Toggle */}
            <Tooltip
              title={
                editorTheme === "vs-dark"
                  ? "Switch to Light Theme"
                  : "Switch to Dark Theme"
              }
              placement="top"
              PopperProps={{
                style: { zIndex: 10000 },
              }}
            >
              <IconButton
                onClick={() =>
                  setEditorTheme(
                    editorTheme === "vs-dark" ? "light" : "vs-dark",
                  )
                }
                size="small"
                sx={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "8px",
                  border: "2px solid rgb(143, 142, 142)",
                  "&:hover": {
                    borderColor: "#00C49A",
                    color: "#00C49A",
                  },
                }}
              >
                {editorTheme === "vs-dark" ? (
                  <LightModeOutlined fontSize="small" />
                ) : (
                  <DarkModeOutlined fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Output Panel Toggle */}
            <Tooltip
              title={showOutput ? "Hide Output Panel" : "Show Output Panel"}
              placement="bottom"
              PopperProps={{
                style: { zIndex: 10000 },
              }}
            >
              <IconButton
                onClick={() => setShowOutput(!showOutput)}
                size="small"
                sx={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "8px",
                  border: "2px solid rgb(143, 142, 142)",
                  "&:hover": {
                    borderColor: "#00C49A",
                    color: "#00C49A",
                  },
                  bgcolor: showOutput ? "#00C49A" : "transparent",
                  color: showOutput ? "#fff" : "inherit",
                }}
              >
                {showOutput ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <Terminal fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Fullscreen Toggle */}
            <Tooltip
              title="Exit Fullscreen (ESC)"
              placement="top"
              PopperProps={{
                style: { zIndex: 10000 },
              }}
            >
              <IconButton
                onClick={toggleFullscreen}
                size="small"
                sx={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "8px",
                  border: "2px solid rgb(143, 142, 142)",
                  "&:hover": {
                    borderColor: "#00C49A",
                    color: "#00C49A",
                  },
                }}
              >
                <CloseFullscreen fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : isMobile ? (
        /* Mobile Header Layout - Compact single row */
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1, // Reduced gap
            mb: 1,
            px: 1,
            // Ensure header doesn't grow huge
            flexShrink: 0,
          }}
        >
          {/* Row 1: Language & Menu */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Language Selector */}
            <FormControl sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={selectedLanguage}
                onChange={(e) => {
                  const newLanguageId = e.target.value;
                  onLanguageChange(newLanguageId);
                  setSelectedLanguage(newLanguageId);
                }}
                disabled={isReadOnly}
                size="small"
                sx={{
                  borderRadius: "8px",
                  height: "40px",
                  fontSize: "14px",
                  bgcolor: "#f5f5f5", // Slight bg distinction
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                {supportedLanguages.map((language) => (
                  <MenuItem
                    key={language.languageId}
                    value={language.languageId}
                  >
                    {language.languageName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Menu Button */}
            <IconButton
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
              sx={{
                width: "40px",
                height: "40px",
                bgcolor: "#f5f5f5",
                borderRadius: "8px",
                flexShrink: 0,
              }}
            >
              <MoreVert />
            </IconButton>
          </Box>

          {/* Row 2: Attempts & Status */}
          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bgcolor: attemptsExhausted ? "#FFEBEE" : "#FFF3E0",
                color: attemptsExhausted ? "#C62828" : "#F57C00",
                px: "12px",
                height: "36px", // Slightly smaller
                borderRadius: "8px",
                border: attemptsExhausted
                  ? "1px solid #FFCDD2"
                  : "1px solid #FFE0B2",
                cursor: "pointer",
                flex: 1,
                justifyContent: "center",
              }}
              onClick={() => {
                setIsAutoOpened(false);
                setOpenAttemptsInfoModal(true);
              }}
            >
              <AccessTimeOutlined sx={{ fontSize: "18px" }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {attemptsUsed}/{maxAttempts} Attempts
              </Typography>
            </Box>
          </Box>

          {/* Mobile Menu (Same as before) */}
          {/* Mobile Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={() => setMobileMenuAnchor(null)}
            PaperProps={{
              sx: {
                minWidth: "200px",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                toggleFullscreen();
                setMobileMenuAnchor(null);
              }}
            >
              <OpenInFull sx={{ mr: 1 }} />
              Full Screen
            </MenuItem>
            <MenuItem
              onClick={() => {
                onFormatCode();
                setMobileMenuAnchor(null);
              }}
            >
              <FormatIndentIncrease sx={{ mr: 1 }} />
              Format Code
            </MenuItem>
            <MenuItem
              onClick={() => {
                setEditorTheme(editorTheme === "vs-dark" ? "light" : "vs-dark");
                setMobileMenuAnchor(null);
              }}
            >
              {editorTheme === "vs-dark" ? (
                <LightModeOutlined sx={{ mr: 1 }} />
              ) : (
                <DarkModeOutlined sx={{ mr: 1 }} />
              )}
              {editorTheme === "vs-dark" ? "Switch to Light" : "Switch to Dark"}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowOutput(!showOutput);
                setMobileMenuAnchor(null);
              }}
            >
              <Terminal sx={{ mr: 1 }} />
              {showOutput ? "Hide Output" : "Show Output"}
            </MenuItem>
            <Box sx={{ px: 2, py: 1, borderTop: "1px solid #e0e0e0" }}>
              <SavedBadge saveState={saveState} lastSavedAt={lastSavedAt} />
            </Box>
          </Menu>

          {/* Row 3: Action Buttons (Side-by-Side) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              size="small"
              onClick={onRunCode}
              disabled={isRunningCode || isSubmitting || attemptsExhausted}
              sx={{
                bgcolor:
                  isRunningCode || isSubmitting || attemptsExhausted
                    ? "#E0E0E0"
                    : "#1976D2",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                height: "36px",
                flex: 1, // Expand to fill half width
                minWidth: 0,
                px: 1,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor:
                    isRunningCode || isSubmitting || attemptsExhausted
                      ? "#E0E0E0"
                      : "#1565C0",
                },
                "&:disabled": {
                  bgcolor: "#E0E0E0",
                  color: "#9E9E9E",
                },
              }}
            >
              {isRunningCode ? "Running..." : "Run"}
            </Button>
            <Button
              variant="contained"
              startIcon={
                <Send
                  sx={{
                    height: { base: "12px", xs: "14px" },
                    width: { base: "12px", xs: "14px" },
                  }}
                />
              }
              size="small"
              onClick={onSubmit}
              disabled={isRunningCode || isSubmitting || !!isAnswered}
              sx={{
                bgcolor: isRunningCode || isSubmitting ? "#E0E0E0" : "#00C49A",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                height: "36px",
                flex: 1, // Expand to fill half width
                minWidth: 0,
                px: 1,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor:
                    isRunningCode || isSubmitting ? "#E0E0E0" : "#00A085",
                },
                "&:disabled": {
                  bgcolor: "#E0E0E0",
                  color: "#9E9E9E",
                },
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </Box>
      ) : (
        /* Normal Mode Header Layout - Original two-row layout */
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end", // Changed to flex-end for clarity, "right" works but flex-end is standard
              alignItems: "center",
              mb: 2.5,
              gap: 1,
            }}
          >
            {/* Status Indicators */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* ✅ REMOVED: Total Time and Timer displays */}
              {/* Programming questions no longer have per-question time limits */}

              {/* Attempts */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  bgcolor: attemptsExhausted ? "#FFEBEE" : "#FFF3E0",
                  color: attemptsExhausted ? "#C62828" : "#F57C00",
                  px: "10px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "16px",
                  border: attemptsExhausted
                    ? "1px solid #FFCDD2"
                    : "1px solid #FFE0B2",
                }}
              >
                <AccessTimeOutlined sx={{ fontSize: "20px" }} />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {attemptsUsed}/{maxAttempts} Attempts
                </Typography>
              </Box>

              {/* Toggle theme button & Last Saved Time */}
              <SavedBadge saveState={saveState} lastSavedAt={lastSavedAt} />

              <Tooltip title="Attempts Info" arrow placement="top">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: attemptsExhausted ? "#C62828" : "#F57C00",
                    px: "8px",
                    height: "32px",
                    minWidth: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    cursor: "pointer",
                    border: "1px solid #FFE0B2",
                  }}
                  onClick={() => {
                    setIsAutoOpened(false);
                    setOpenAttemptsInfoModal(true);
                  }}
                >
                  <InfoOutlined sx={{ fontSize: "20px" }} />
                </Box>
              </Tooltip>

              {/* Format Code Button - Moved here */}
              <Tooltip
                title="Format Code (Ctrl+Shift+F)"
                placement="top"
                arrow
                PopperProps={{
                  style: { zIndex: 10000 },
                }}
              >
                <IconButton
                  onClick={onFormatCode}
                  disabled={isReadOnly}
                  size="small"
                  sx={{
                    height: "30px",
                    width: "32px",
                    borderRadius: "8px",
                    border: "2px solid rgb(143, 142, 142)",
                    "&:hover": {
                      borderColor: "#00C49A",
                      color: "#00C49A",
                    },
                  }}
                >
                  <FormatIndentIncrease fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Fullscreen Toggle Button */}
              <Tooltip
                title="Enter Fullscreen"
                placement="top"
                arrow
                PopperProps={{
                  style: { zIndex: 10000 },
                }}
              >
                <IconButton
                  onClick={toggleFullscreen}
                  size="small"
                  sx={{
                    height: "30px",
                    width: "32px",
                    borderRadius: "8px",
                    border: "2px solid rgb(143, 142, 142)",
                    "&:hover": {
                      borderColor: "#00C49A",
                      color: "#00C49A",
                    },
                  }}
                >
                  <OpenInFull fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  editorTheme === "vs-dark"
                    ? "Switch to Light Theme"
                    : "Switch to Dark Theme"
                }
                placement="top"
                arrow
                PopperProps={{
                  style: { zIndex: 10000 },
                }}
              >
                <IconButton
                  onClick={() =>
                    setEditorTheme(
                      editorTheme === "vs-dark" ? "light" : "vs-dark",
                    )
                  }
                  size="small"
                  sx={{
                    height: "30px",
                    width: "32px",
                    borderRadius: "8px",
                    border: "2px solid rgb(143, 142, 142)",
                    "&:hover": {
                      borderColor: "#00C49A",
                      color: "#00C49A",
                    },
                  }}
                >
                  {editorTheme === "vs-dark" ? (
                    <LightModeOutlined fontSize="small" />
                  ) : (
                    <DarkModeOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Status Bar with Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Language Selector */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "left",
                }}
              >
                Language:
              </Typography>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => {
                    const newLanguageId = e.target.value;
                    onLanguageChange(newLanguageId);
                    setSelectedLanguage(newLanguageId);
                  }}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    height: "32px",
                  }}
                >
                  {supportedLanguages.map((language) => (
                    <MenuItem
                      key={language.languageId}
                      value={language.languageId}
                    >
                      {language.languageName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={onRunCode}
                disabled={
                  isRunningCode ||
                  isSubmitting ||
                  isReadOnly ||
                  attemptsExhausted
                }
                sx={{
                  bgcolor:
                    isRunningCode ||
                    isSubmitting ||
                    isReadOnly ||
                    attemptsExhausted
                      ? "#E0E0E0"
                      : "#1976D2",
                  color: "#fff",
                  width: "fit-content",
                  fontWeight: 600,
                  fontSize: "14px",
                  borderRadius: "8px",
                  height: "32px",
                  minWidth: "fit-content",
                  px: 2,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor:
                      isRunningCode ||
                      isSubmitting ||
                      isReadOnly ||
                      attemptsExhausted
                        ? "#E0E0E0"
                        : "#1565C0",
                  },
                  "&:disabled": {
                    bgcolor: "#E0E0E0",
                    color: "#9E9E9E",
                  },
                }}
              >
                {isRunningCode ? "Running..." : "Run Code"}
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={onSubmit}
                disabled={isRunningCode || isSubmitting || !!isAnswered}
                sx={{
                  bgcolor:
                    isRunningCode || isSubmitting || !!isAnswered
                      ? "#E0E0E0"
                      : "#00C49A",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "14px",
                  borderRadius: "8px",
                  height: "32px",
                  width: "fit-content",
                  minWidth: "fit-content",
                  px: 2,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor:
                      isRunningCode || isSubmitting || !!isAnswered
                        ? "#E0E0E0"
                        : "#00A085",
                  },
                  "&:disabled": {
                    bgcolor: "#E0E0E0",
                    color: "#9E9E9E",
                  },
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Editor and Output Container - Horizontal in fullscreen, vertical otherwise */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: isEditorFullscreen ? "row" : "column",
          gap: isEditorFullscreen ? 1 : 0,
          overflow: isEditorFullscreen ? "hidden" : "visible",
          position: "relative",
          px: isEditorFullscreen ? 1 : 0,
        }}
      >
        {/* Editor Section - Flexible height/width */}
        <Box
          sx={{
            flex: 1,
            minHeight: isMobile ? "300px" : "200px",
            minWidth: isEditorFullscreen ? `${MIN_EDITOR_WIDTH}px` : 0,
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e0e0e0",
            mb: isEditorFullscreen ? 0 : isMobile ? 0.5 : 0,
            position: "relative",
            height: isEditorFullscreen ? "100%" : "auto",
            width: isEditorFullscreen
              ? showOutput && !isOutputFloating
                ? `${editorWidthPercent}%`
                : "100%"
              : "100%",
            transition: isResizing ? "none" : "width 0.3s ease-in-out",
            flexShrink: 1,
            overflow: "hidden",
          }}
        >
          {/* Loading Overlay */}
          {isRunningCode && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#00C49A" }} />
              <Typography
                sx={{
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Running your code...
              </Typography>
            </Box>
          )}

          {/* Monaco Editor */}
          <Editor
            height="100%"
            language={monacoLanguage}
            theme={editorTheme}
            defaultValue={code}
            options={editorOptions}
            onChange={(val) => {
              const newValue = val ?? "";

              isEditorChangeRef.current = true;
              editorValueRef.current = newValue;

              setCode(newValue);

              if (resetFlagTimeoutRef.current) {
                clearTimeout(resetFlagTimeoutRef.current);
              }

              resetFlagTimeoutRef.current = setTimeout(() => {
                isEditorChangeRef.current = false;
                resetFlagTimeoutRef.current = null;
              }, 300);
            }}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;

              const model = editor.getModel();
              if (model) {
                if (code) {
                  model.setValue(code);
                  editorValueRef.current = code;
                }

                if (monacoLanguage) {
                  monaco.editor.setModelLanguage(model, monacoLanguage);
                }
              }

              const initializeTypingAnalysis = () => {
                // Try to get the DOM node immediately
                let editorContainer = editor.getDomNode();
                const questionContainer = document.querySelector(
                  ".question-container",
                );

                // If not available immediately, try again inside the timeout
                setTimeout(() => {
                  try {
                    // Refetch in case it wasn't compliant before or has changed
                    if (
                      !editorContainer ||
                      !document.body.contains(editorContainer)
                    ) {
                      editorContainer = editor.getDomNode();
                    }

                    if (
                      editorContainer &&
                      typingAnalyzer &&
                      typeof typingAnalyzer.startTracking === "function" &&
                      (!typingAnalyzer.isTracking ||
                        !typingAnalyzer.isTracking())
                    ) {
                      typingAnalyzer.startTracking(
                        editorContainer,
                        questionContainer || componentContainerRef.current,
                      );
                    }
                  } catch (err) {
                    console.warn(
                      "Retrying typing analyzer initialization failed:",
                      err,
                    );
                  }
                }, 2000);
              };

              // Initial delay to ensure editor is fully mounted
              setTimeout(() => {
                initializeTypingAnalysis();
              }, 5000);
              editor.onDidChangeModelContent((e) => {
                const changes = e.changes.map((change) => ({
                  range: change.range,
                  text: change.text,
                  rangeLength: change.rangeLength,
                  isUndoing: change.isUndoing || false,
                  isRedoing: change.isRedoing || false,
                }));

                trackEditorEvent(
                  "change",
                  {
                    changes: changes,
                    versionId: editor.getModel()?.getVersionId(),
                  },
                  CHANGE_THROTTLE_MS,
                  lastChangeEventTimeRef,
                );
              });

              editor.onDidChangeCursorPosition((e) => {
                trackEditorEvent(
                  "cursor",
                  {
                    position: e.position,
                    reason: e.reason || "unknown",
                  },
                  CURSOR_THROTTLE_MS,
                  lastCursorEventTimeRef,
                );
              });

              editor.onDidChangeCursorSelection((e) => {
                trackEditorEvent(
                  "selection",
                  {
                    selection: e.selection,
                    source: e.source || "unknown",
                  },
                  SELECTION_THROTTLE_MS,
                  lastSelectionEventTimeRef,
                );
              });

              editor.onDidFocusEditorWidget(() => {
                trackEditorEvent(
                  "focus",
                  { focused: true },
                  FOCUS_THROTTLE_MS,
                  lastFocusEventTimeRef,
                );
              });

              editor.onDidBlurEditorWidget(() => {
                trackEditorEvent(
                  "blur",
                  { focused: false },
                  FOCUS_THROTTLE_MS,
                  lastFocusEventTimeRef,
                );
              });

              editor.onDidLayoutChange((e) => {
                trackEditorEvent(
                  "layout",
                  {
                    width: e.width,
                    height: e.height,
                  },
                  2000,
                  lastFocusEventTimeRef,
                );
              });

              editor.onDidScrollChange((e) => {
                trackEditorEvent(
                  "scroll",
                  {
                    scrollTop: e.scrollTop,
                    scrollLeft: e.scrollLeft,
                  },
                  500,
                  lastFocusEventTimeRef,
                );
              });

              const addFormattingActions = () => {
                editor.addAction({
                  id: "format-code",
                  label: "Format Code",
                  keybindings: [
                    monaco.KeyMod.CtrlCmd |
                      monaco.KeyMod.Shift |
                      monaco.KeyCode.KeyF,
                  ],
                  contextMenuGroupId: "1_modification",
                  contextMenuOrder: 1.5,
                  run: async () => {
                    const success = await formatCode(editor, monaco);
                  },
                });

                editor.addAction({
                  id: "format-selection",
                  label: "Format Selection",
                  keybindings: [
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK,
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF,
                  ],
                  contextMenuGroupId: "1_modification",
                  contextMenuOrder: 1.6,
                  run: async () => {
                    const selection = editor.getSelection();
                    if (selection && !selection.isEmpty()) {
                      const success = await formatSelection(editor, monaco);
                    }
                  },
                });

                editor.addAction({
                  id: "save-with-format",
                  label: "Save with Format",
                  keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
                  run: async () => {
                    const success = await formatCode(editor, monaco);
                  },
                });
              };

              addFormattingActions();

              registerLanguageFormatters(monaco);
              setIsMonacoMounted(true);
            }}
          />
        </Box>

        {/* Show Output Button - Only show when not in fullscreen and not mobile */}
        {!isEditorFullscreen && !isMobile && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {!showOutput && (
              <Button
                variant="outlined"
                startIcon={<ExpandMore />}
                onClick={() => setShowOutput(true)}
                sx={{
                  borderColor: "#e0e0e0",
                  color: "#666",
                  textTransform: "none",
                  borderRadius: "8px",
                  height: "36px",
                  minWidth: "fit-content",
                  width: "fit-content",
                  px: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#00C49A",
                    color: "#00C49A",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0, 196, 154, 0.2)",
                  },
                }}
              >
                Show Output
              </Button>
            )}
          </Box>
        )}

        {/* Resizer - Only show in fullscreen mode when output is visible */}
        {isEditorFullscreen && showOutput && !isOutputFloating && (
          <Box
            onMouseDown={handleResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize editor and output panels"
            title="Drag to resize"
            sx={{
              width: "6px",
              height: "100%",
              cursor: "col-resize",
              backgroundColor: isResizing
                ? "rgba(0, 196, 154, 0.3)"
                : "transparent",
              borderLeft: "1px solid #e0e0e0",
              borderRight: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              transition: "background-color 0.2s ease",
              flexShrink: 0,
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(0, 196, 154, 0.2)",
              },
            }}
          />
        )}

        {/* Output Panel - Right side in fullscreen, bottom overlay otherwise, fullscreen modal on mobile */}
        {!isOutputFloating && (
          <Box
            sx={{
              ...(isMobile && showOutput
                ? {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 100,
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                  }
                : isEditorFullscreen
                  ? {
                      width: showOutput ? `${100 - editorWidthPercent}%` : 0,
                      minWidth: showOutput ? `${MIN_OUTPUT_WIDTH}px` : 0,
                      height: "100%",
                      borderLeft: "none",
                      display: showOutput ? "flex" : "none",
                      flexDirection: "column",
                      overflow: "hidden",
                      transition: isResizing
                        ? "none"
                        : "width 0.3s ease-in-out",
                      flexShrink: 0,
                    }
                  : {
                      height: showOutput ? outputHeight : 0,
                      flexShrink: 0,
                      display: showOutput ? "flex" : "none",
                      flexDirection: "column",
                      overflow: "hidden",
                      transition: isResizing ? "none" : "height 0.3s ease-in-out",
                      mt: showOutput ? 1 : 0,
                    }),
            }}
          >
            <OutputPanel
              showOutput={showOutput}
              setShowOutput={setShowOutput}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              testCases={testCases}
              customInput={customInput}
              setCustomInput={setCustomInput}
              customResult={customResult}
              customLoading={customLoading}
              onRunCustomInput={onRunCustomInput}
              results={results}
              summary={summary}
              showCelebration={showCelebration}
              setShowCelebration={setShowCelebration}
              isOutputFloating={isOutputFloating}
              onFloatingToggle={onFloatingToggle}
              vtLoading={vtLoading}
              outputHeight={
                isMobile ? "100%" : isEditorFullscreen ? "100%" : outputHeight
              }
              isEditorFullscreen={isEditorFullscreen}
              isMobile={isMobile}
            />
          </Box>
        )}
      </Box>

      <AttemptsInfoModal
        open={openAttemptsInfoModal}
        onClose={() => setOpenAttemptsInfoModal(false)}
        attemptsUsed={attemptsUsed}
        maxAttempts={maxAttempts}
        attemptsExhausted={attemptsExhausted}
        isAutoOpened={isAutoOpened}
        onGoToCustomInput={() => {
          setOpenAttemptsInfoModal(false);
          setActiveTab(1);
          setShowOutput(true);
        }}
      />
    </Box>
  );
};

export default EditorSection;
